import React, { useState, useCallback, useEffect, useMemo } from 'react';
import {
  Box, Typography, Button, Alert, Snackbar, CircularProgress,
  IconButton, Chip, Drawer, Tooltip, alpha,
  Badge, Paper,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import TuneIcon from '@mui/icons-material/Tune';
import CloseIcon from '@mui/icons-material/Close';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import ShieldIcon from '@mui/icons-material/Shield';
import BoltIcon from '@mui/icons-material/Bolt';
import BarChartIcon from '@mui/icons-material/BarChart';
import SignalCellularAltIcon from '@mui/icons-material/SignalCellularAlt';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import HistoryIcon from '@mui/icons-material/History';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { scannerAPI, marketCorrectionAPI } from '../services/api';
import { ScannerFilters } from '../components/scanner';
import { getPatternIcon, getPatternLabel } from '../components/scanner/PatternIcons';
import AIConfidenceBadge from '../components/AIConfidenceBadge';

// ── Design tokens ──
const scoreColor = (s) =>
  s >= 8 ? '#2e7d32' : s >= 6 ? '#667eea' : s >= 4 ? '#e65100' : '#c62828';

const fmt = (n) => {
  if (n == null || isNaN(n)) return '-';
  if (n >= 1e12) return `${(n / 1e12).toFixed(1)}T`;
  if (n >= 1e9) return `${(n / 1e9).toFixed(1)}B`;
  if (n >= 1e6) return `${(n / 1e6).toFixed(1)}M`;
  if (n >= 1e3) return `${(n / 1e3).toFixed(1)}K`;
  return n.toFixed?.(1) ?? n;
};

const REGIME_MAP = {
  ALLOW: { color: '#2e7d32', bg: '#e8f5e9', labelKey: 'scanner.regime.ALLOW', icon: <CheckCircleIcon /> },
  RISK_ON: { color: '#2e7d32', bg: '#e8f5e9', labelKey: 'scanner.regime.RISK_ON', icon: <CheckCircleIcon /> },
  WATCH: { color: '#5c6bc0', bg: '#e8eaf6', labelKey: 'scanner.regime.WATCH', icon: <RemoveCircleOutlineIcon /> },
  NEUTRAL: { color: '#5c6bc0', bg: '#e8eaf6', labelKey: 'scanner.regime.NEUTRAL', icon: <RemoveCircleOutlineIcon /> },
  AVOID: { color: '#c62828', bg: '#ffebee', labelKey: 'scanner.regime.AVOID', icon: <CancelIcon /> },
  RISK_OFF: { color: '#c62828', bg: '#ffebee', labelKey: 'scanner.regime.RISK_OFF', icon: <CancelIcon /> },
};

// ── Stat Card ──
const StatCard = ({ label, value, color = '#1e293b', sub, icon, bg }) => (
  <Box sx={{
    flex: '1 1 0', minWidth: 140, bgcolor: bg || '#fff', borderRadius: '6px',
    boxShadow: '0 1px 3px rgba(0,0,0,0.08)', overflow: 'hidden',
    display: 'flex', alignItems: 'center', gap: 1.5, px: 2, py: 1.5,
  }}>
    {icon && (
      <Box sx={{
        width: 40, height: 40, borderRadius: 1.5, display: 'flex', alignItems: 'center', justifyContent: 'center',
        bgcolor: alpha(color, 0.12), flexShrink: 0,
      }}>
        {React.cloneElement(icon, { sx: { fontSize: 20, color } })}
      </Box>
    )}
    <Box sx={{ minWidth: 0 }}>
      <Typography sx={{ fontSize: '0.6rem', fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: 0.5, lineHeight: 1, mb: 0.4 }}>
        {label}
      </Typography>
      <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5 }}>
        <Typography sx={{ fontWeight: 900, fontSize: '1.5rem', fontFamily: 'ui-monospace, monospace', color, lineHeight: 1 }}>
          {value}
        </Typography>
        {sub && <Typography sx={{ fontSize: '0.62rem', color: '#94a3b8', whiteSpace: 'nowrap' }}>{sub}</Typography>}
      </Box>
    </Box>
  </Box>
);

// ── Risk Gauge ──
const RiskGauge = ({ probability, horizon }) => {
  const { t } = useTranslation();
  const pct = Math.min(probability * 100, 100);
  const color = pct >= 50 ? '#c62828' : pct >= 30 ? '#e65100' : '#2e7d32';
  return (
    <Tooltip title={t('scanner.risk.correctionProb', { horizon, defaultValue: `Xác suất điều chỉnh trong ${horizon} ngày` })} arrow>
      <Box sx={{ minWidth: 64, textAlign: 'center' }}>
        <Typography sx={{ fontSize: '0.62rem', fontWeight: 700, color: '#94a3b8', mb: 0.5 }}>{horizon} {t('scanner.risk.days', { defaultValue: 'ngày' })}</Typography>
        <Box sx={{ height: 5, bgcolor: '#f0f0f0', borderRadius: 3, overflow: 'hidden' }}>
          <Box sx={{ height: '100%', width: `${pct}%`, bgcolor: color, borderRadius: 3, transition: 'width 0.6s ease' }} />
        </Box>
        <Typography sx={{ fontSize: '0.82rem', fontWeight: 800, color, fontFamily: 'monospace', mt: 0.4, lineHeight: 1 }}>
          {pct.toFixed(0)}%
        </Typography>
      </Box>
    </Tooltip>
  );
};

// ── Confidence config ──
const CONFIDENCE_MAP = {
  HIGH: { color: '#2e7d32', bg: '#e8f5e9' },
  MEDIUM: { color: '#e65100', bg: '#fff3e0' },
  LOW: { color: '#c62828', bg: '#ffebee' },
};

// ── Signal Row ──
const SignalRow = ({ data, index, onClick, t, language }) => {
  const score = data.combined_score || data.score || 0;
  const color = scoreColor(score);
  const expColor = data.expectancy_pct > 2 ? '#2e7d32' : data.expectancy_pct > 0 ? '#667eea' : '#c62828';
  const conf = data.confidence ? CONFIDENCE_MAP[data.confidence] || CONFIDENCE_MAP.LOW : null;

  return (
    <Box onClick={onClick} sx={{
      display: 'flex', alignItems: 'center', gap: 1.5, px: 2, py: 1,
      cursor: 'pointer', borderBottom: '1px solid #f0f0f0',
      transition: 'all 0.15s',
      '&:hover': { bgcolor: '#f8fafc' },
    }}>
      <Typography sx={{ fontSize: '0.72rem', fontWeight: 600, color: '#94a3b8', width: 24, textAlign: 'center' }}>
        {index + 1}
      </Typography>
      <Box sx={{ width: 4, height: 28, borderRadius: 2, bgcolor: color, flexShrink: 0 }} />
      <Box sx={{ flex: 1, minWidth: 120 }}>
        <Typography sx={{ fontWeight: 800, fontSize: '0.85rem', color: '#667eea' }}>
          {data.ticker}
        </Typography>
        <Typography sx={{ fontSize: '0.6rem', color: '#94a3b8', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {data.companyName || ''}
        </Typography>
      </Box>
      <Typography sx={{ fontSize: '0.68rem', fontWeight: 600, color: '#64748b', width: 120, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
        {data.sector_name?.vi || '-'}
      </Typography>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, width: 160, minWidth: 0 }}>
        <Box sx={{ color: '#667eea', display: 'flex', flexShrink: 0, '& svg': { fontSize: 14 } }}>
          {getPatternIcon(data.signal_type || data.pattern_type)}
        </Box>
        <Tooltip
          title={data.note ? <Box sx={{ whiteSpace: 'pre-line', fontSize: 11, lineHeight: 1.5, maxWidth: 280 }}>{data.note}</Box> : ''}
          arrow placement="top" disableHoverListener={!data.note}
        >
          <Typography sx={{ fontSize: '0.72rem', fontWeight: 600, color: '#475569', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', cursor: data.note ? 'help' : 'default' }}>
            {getPatternLabel(data.signal_type || data.pattern_type)}
          </Typography>
        </Tooltip>
        {/* Trend & RS micro-indicators */}
        <Box sx={{ display: 'flex', gap: '2px', flexShrink: 0, ml: 'auto' }}>
          <Tooltip title={data.trend_ok ? t('scanner.indicators.trendOk') : t('scanner.indicators.trendFail')} arrow>
            <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: data.trend_ok ? '#2e7d32' : '#e0e0e0' }} />
          </Tooltip>
          <Tooltip title={data.rs_ok ? t('scanner.indicators.rsOk') : t('scanner.indicators.rsFail')} arrow>
            <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: data.rs_ok ? '#667eea' : '#e0e0e0' }} />
          </Tooltip>
        </Box>
      </Box>
      {/* Score with breakdown tooltip */}
      <Tooltip
        title={data.score_components ? (
          <Box sx={{ fontSize: 11, lineHeight: 1.6, p: 0.5 }}>
            <Typography sx={{ fontWeight: 700, fontSize: 11, mb: 0.5 }}>{t('scanner.scoreComponents.title')}</Typography>
            {Object.entries(data.score_components).map(([k, v]) => (
              <Box key={k} sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
                <span>{t(`scanner.scoreComponents.${k}`, k)}</span>
                <span style={{ fontFamily: 'monospace', fontWeight: 700 }}>{typeof v === 'number' ? (v * 100).toFixed(0) + '%' : v}</span>
              </Box>
            ))}
          </Box>
        ) : ''}
        arrow placement="top" disableHoverListener={!data.score_components}
        slotProps={{ tooltip: { sx: { maxWidth: 200, bgcolor: '#1e293b', borderRadius: '8px', px: 1.5, py: 1 } } }}
      >
        <Typography sx={{ fontWeight: 900, fontSize: '0.9rem', color, fontFamily: 'monospace', width: 40, textAlign: 'center', cursor: data.score_components ? 'help' : 'default' }}>
          {score.toFixed(1)}
        </Typography>
      </Tooltip>
      {/* Confidence badge */}
      {conf && (
        <Typography sx={{
          fontSize: '0.55rem', fontWeight: 800, color: conf.color, bgcolor: conf.bg,
          px: 0.75, py: 0.15, borderRadius: '4px', lineHeight: 1.2, width: 28, textAlign: 'center',
        }}>
          {t(`scanner.confidence.${data.confidence}`)}
        </Typography>
      )}
      {/* AI confidence badge */}
      <Box sx={{ minWidth: 96, display: 'flex', justifyContent: 'center' }}>
        <AIConfidenceBadge
          confidence={data.ai_confidence}
          language={language}
          tooltipTitle={t(
            'scanner.table.aiConfTooltip',
            'AI confidence \u2014 pattern reliability win probability',
          )}
          compact
        />
      </Box>
      <Box sx={{ display: 'flex', gap: 1, width: 90 }}>
        <Typography sx={{ fontSize: '0.68rem', fontWeight: 700, color: data.score ? scoreColor(data.score) : '#94a3b8', fontFamily: 'monospace', width: 32 }}>
          {data.score?.toFixed(1) || '-'}
        </Typography>
        <Typography sx={{ fontSize: '0.68rem', fontWeight: 700, color: data.volume_score ? scoreColor(data.volume_score) : '#94a3b8', fontFamily: 'monospace', width: 32 }}>
          {data.volume_score?.toFixed(1) || '-'}
        </Typography>
      </Box>
      <Box sx={{ width: 56, textAlign: 'right' }}>
        {data.expectancy_pct != null ? (
          <>
            <Typography sx={{ fontSize: '0.75rem', fontWeight: 800, color: expColor, fontFamily: 'monospace', lineHeight: 1 }}>
              {data.expectancy_pct > 0 ? '+' : ''}{data.expectancy_pct.toFixed(1)}%
            </Typography>
            <Typography sx={{ fontSize: '0.55rem', color: '#94a3b8', lineHeight: 1.2 }}>
              P:{(data.p_win * 100).toFixed(0)}%
            </Typography>
          </>
        ) : (
          <Typography sx={{ fontSize: '0.68rem', color: '#94a3b8' }}>-</Typography>
        )}
      </Box>
      <Typography sx={{ fontWeight: 700, fontSize: '0.82rem', color: '#1e293b', fontFamily: 'monospace', width: 70, textAlign: 'right' }}>
        {(data.current_close || data.close)?.toLocaleString('vi-VN') || '-'}
      </Typography>
      <Box sx={{ width: 70, textAlign: 'right' }}>
        {data.details?.stop_loss ? (
          <>
            <Typography sx={{ fontSize: '0.72rem', fontWeight: 600, color: '#c62828', fontFamily: 'monospace', lineHeight: 1 }}>
              {Math.round(data.details.stop_loss).toLocaleString('vi-VN')}
            </Typography>
            <Typography sx={{ fontSize: '0.55rem', color: '#c62828', lineHeight: 1.2 }}>
              {(() => { const price = data.current_close || data.close; return price ? `-${(((price - data.details.stop_loss) / price) * 100).toFixed(1)}%` : ''; })()}
            </Typography>
          </>
        ) : <Typography sx={{ fontSize: '0.68rem', color: '#94a3b8' }}>-</Typography>}
      </Box>
      <Box sx={{ width: 70, textAlign: 'right' }}>
        {data.details?.target_price ? (
          <>
            <Typography sx={{ fontSize: '0.72rem', fontWeight: 600, color: '#2e7d32', fontFamily: 'monospace', lineHeight: 1 }}>
              {Math.round(data.details.target_price).toLocaleString('vi-VN')}
            </Typography>
            <Typography sx={{ fontSize: '0.55rem', color: '#2e7d32', lineHeight: 1.2 }}>
              {(() => { const price = data.current_close || data.close; return price ? `+${(((data.details.target_price - price) / price) * 100).toFixed(1)}%` : ''; })()}
            </Typography>
          </>
        ) : <Typography sx={{ fontSize: '0.68rem', color: '#94a3b8' }}>-</Typography>}
      </Box>
      <Typography sx={{ fontSize: '0.72rem', fontWeight: 600, color: '#64748b', fontFamily: 'monospace', width: 56, textAlign: 'right' }}>
        {fmt(data.volume)}
      </Typography>
    </Box>
  );
};


// ═══════════════════════════════════════════════════════
// MAIN COMPONENT
// ═══════════════════════════════════════════════════════
const StockScanner = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [marketStatus, setMarketStatus] = useState(null);
  const [scanResults, setScanResults] = useState(null);
  const [error, setError] = useState(null);
  const [multiHorizonData, setMultiHorizonData] = useState(null);
  const [patternPerformance, setPatternPerformance] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [sortBy, setSortBy] = useState('score');
  const [showRiskDetail, setShowRiskDetail] = useState(false);
  const [lastScanTime, setLastScanTime] = useState(null);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [historyTab, setHistoryTab] = useState('overview'); // 'overview' | 'sessions' | 'patterns'
  const [historySessions, setHistorySessions] = useState([]);
  const [patternAccuracy, setPatternAccuracy] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [selectedSession, setSelectedSession] = useState(null);
  const [sessionSignals, setSessionSignals] = useState([]);
  const [historySummary, setHistorySummary] = useState(null);

  const [filters, setFilters] = useState({
    patterns: [
      'THROWBACK', 'BULL_FLAG', 'TIGHT_BASE_BREAKOUT', 'BREAKOUT_RETEST', 'DOUBLE_BOTTOM',
      'TIGHT_BASE_NEW', 'VCP', 'ASCENDING_TRIANGLE', 'CUP_HANDLE', 'INVERSE_HS',
      'BULL_PENNANT', 'SHAKEOUT', 'MA_PULLBACK', 'WYCKOFF_SPRING', 'ASCENDING_BASE',
    ],
    min_score: 5.0, min_volume_score: 5.0, use_volume_analysis: true,
    use_weekly_gate: false, use_vn_filters: false, pattern_mode: 'breakout',
    min_liquidity: 10000000000, max_results: 50,
  });

  const fetchMarketStatus = useCallback(async () => {
    try {
      const [st, pe, mh] = await Promise.allSettled([
        scannerAPI.getMarketStatus(),
        scannerAPI.getPatternPerformance(),
        marketCorrectionAPI.getMultiHorizon(),
      ]);
      if (st.status !== 'fulfilled') throw st.reason;
      setPatternPerformance(pe.status === 'fulfilled' ? pe.value?.data?.data || null : null);
      setMultiHorizonData(mh.status === 'fulfilled' && mh.value?.data?.success ? mh.value.data : null);
      setMarketStatus(st.value.data);
    } catch (err) {
      console.error('Failed to fetch market status:', err);
      setError(t('scanner.errors.marketStatusFailed', 'Không thể tải trạng thái thị trường'));
    }
  }, [t]);

  const handleScan = useCallback(async () => {
    if (filters.patterns.length === 0) {
      setSnackbar({ open: true, message: t('scanner.errors.noPattern', 'Vui lòng chọn ít nhất một mẫu hình'), severity: 'warning' });
      return;
    }
    setLoading(true); setError(null); setScanResults(null);
    try {
      await fetchMarketStatus();
      const response = await scannerAPI.scanBullish({
        scan_types: filters.patterns,
        min_avg_value_20: filters.min_liquidity || 100_000_000,
        min_price: 5000, vol_mult: 1.5, max_dd: 0.25,
        require_market_ok: false, use_trend_template: false, use_rs_filter: false,
        use_volume_analysis: filters.use_volume_analysis,
        use_weekly_gate: filters.use_weekly_gate, use_vn_filters: filters.use_vn_filters,
        pattern_mode: filters.pattern_mode, limit: filters.max_results,
      });
      setScanResults(response.data);
      setLastScanTime(new Date());
      const c = response.data.results?.length || 0;
      setSnackbar({ open: true, message: t('scanner.success.found', { count: c, total: response.data.total_scanned || 0 }), severity: c > 0 ? 'success' : 'info' });
    } catch (err) {
      const msg = err.response?.data?.detail || err.message || t('scanner.errors.scanFailed', 'Quét thất bại');
      setError(msg);
      setSnackbar({ open: true, message: msg, severity: 'error' });
    } finally {
      setLoading(false);
    }
  }, [filters, fetchMarketStatus, t]);

  // Only auto-scan on initial mount, not on every filter change
  const initialScanDone = React.useRef(false);
  useEffect(() => {
    if (!initialScanDone.current) {
      initialScanDone.current = true;
      handleScan();
    }
  }, [handleScan]);

  const openHistory = useCallback(async () => {
    setHistoryOpen(true);
    setHistoryLoading(true);
    try {
      const [sessRes, patRes, sumRes] = await Promise.allSettled([
        scannerAPI.getHistorySessions(30),
        scannerAPI.getPatternAccuracy(),
        scannerAPI.getHistorySummary(),
      ]);
      if (sessRes.status === 'fulfilled') setHistorySessions(sessRes.value.data?.sessions || []);
      if (patRes.status === 'fulfilled') setPatternAccuracy(patRes.value.data?.patterns || []);
      if (sumRes.status === 'fulfilled') setHistorySummary(sumRes.value.data || null);
    } catch (e) { console.error(e); }
    setHistoryLoading(false);
  }, []);

  const viewSession = useCallback(async (sessionId) => {
    try {
      const res = await scannerAPI.getHistorySession(sessionId);
      setSelectedSession(res.data?.session);
      setSessionSignals(res.data?.signals || []);
    } catch (e) { console.error(e); }
  }, []);

  // Sorted results
  const sortedResults = useMemo(() => {
    const list = [...(scanResults?.results || [])];
    if (sortBy === 'score') list.sort((a, b) => (b.combined_score || b.score || 0) - (a.combined_score || a.score || 0));
    else if (sortBy === 'expectancy') list.sort((a, b) => (b.expectancy_pct || -999) - (a.expectancy_pct || -999));
    else if (sortBy === 'volume') list.sort((a, b) => (b.volume || 0) - (a.volume || 0));
    return list;
  }, [scanResults, sortBy]);

  // Stats
  const stats = useMemo(() => {
    if (!sortedResults.length) return null;
    const scores = sortedResults.map(r => r.combined_score || r.score || 0);
    const avg = scores.reduce((a, b) => a + b, 0) / scores.length;
    const high = scores.filter(s => s >= 8).length;
    const withExp = sortedResults.filter(r => r.expectancy_pct != null);
    const avgExp = withExp.length ? withExp.reduce((a, r) => a + r.expectancy_pct, 0) / withExp.length : null;
    return { avg, high, avgExp, total: sortedResults.length };
  }, [sortedResults]);

  // Risk horizons
  const riskHorizons = useMemo(() => {
    if (!multiHorizonData?.horizons) return [];
    return [5, 10, 20, 30]
      .filter(h => multiHorizonData.horizons[h]?.available)
      .map(h => ({ h, p: multiHorizonData.horizons[h].p_corr }));
  }, [multiHorizonData]);

  const maxRisk = useMemo(() => riskHorizons.length ? Math.max(...riskHorizons.map(r => r.p)) : 0, [riskHorizons]);



  const regime = REGIME_MAP[marketStatus?.market_regime] || { color: '#64748b', bg: '#f1f5f9', labelKey: null, icon: <WarningAmberIcon /> };
  const regimeLabel = regime.labelKey ? t(regime.labelKey) : (marketStatus?.market_regime || '?');

  return (
    <Box sx={{ p: 3, maxWidth: 1400, mx: 'auto' }}>
      {/* ═══ HEADER ═══ */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          {/* Icon box */}
          <Box sx={{
            width: 42, height: 42, borderRadius: 1.5, display: 'flex', alignItems: 'center', justifyContent: 'center',
            bgcolor: '#667eea',
          }}>
            <SignalCellularAltIcon sx={{ fontSize: 22, color: '#fff' }} />
          </Box>
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography sx={{ fontSize: '1.35rem', fontWeight: 800, color: '#1e293b', letterSpacing: -0.3 }}>
                {t('scanner.header.title')}
              </Typography>
              {lastScanTime && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.6, bgcolor: '#f1f5f9', px: 1, py: 0.3, borderRadius: 1.5 }}>
                  <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: '#667eea', boxShadow: '0 0 6px #667eea' }} />
                  <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, color: '#475569' }}>
                    {lastScanTime.toLocaleDateString('vi-VN')} {lastScanTime.toLocaleTimeString('vi-VN', { hour: '2-digit', minute: '2-digit' })}
                  </Typography>
                </Box>
              )}
            </Box>
            <Typography sx={{ fontSize: '0.75rem', color: '#94a3b8', fontWeight: 500 }}>
              {t('scanner.header.subtitle')}
            </Typography>
          </Box>

          {/* Market regime badge */}
          {marketStatus && (
            <Chip
              icon={React.cloneElement(regime.icon, { sx: { fontSize: 15, color: `${regime.color} !important` } })}
              label={regimeLabel}
              size="small"
              sx={{
                height: 28, fontSize: '0.72rem', fontWeight: 700, ml: 1,
                bgcolor: regime.bg, color: regime.color,
                '& .MuiChip-icon': { ml: 0.5 },
              }}
            />
          )}

          {/* VNIndex */}
          {marketStatus && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, ml: 1 }}>
              <Typography sx={{ fontSize: '0.85rem', fontWeight: 800, color: '#1e293b', fontFamily: 'monospace' }}>
                VNI {marketStatus.vnindex_close?.toLocaleString('vi-VN', { minimumFractionDigits: 2 })}
              </Typography>
              {marketStatus.trend_up !== undefined && (
                marketStatus.trend_up
                  ? <TrendingUpIcon sx={{ fontSize: 16, color: '#2e7d32' }} />
                  : <TrendingDownIcon sx={{ fontSize: 16, color: '#c62828' }} />
              )}
            </Box>
          )}
        </Box>

        {/* Right: Actions */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          {/* Risk pill */}
          {riskHorizons.length > 0 && (
            <Tooltip title={t('scanner.regime.riskAdjust')} arrow>
              <Chip
                icon={<ShieldIcon sx={{ fontSize: 14, color: (maxRisk >= 0.45 ? '#c62828' : maxRisk >= 0.3 ? '#e65100' : '#2e7d32') + ' !important' }} />}
                label={`${(maxRisk * 100).toFixed(0)}%`}
                size="small"
                onClick={() => setShowRiskDetail(v => !v)}
                sx={{
                  height: 28, fontSize: '0.75rem', fontWeight: 700, cursor: 'pointer',
                  bgcolor: maxRisk >= 0.45 ? '#ffebee' : maxRisk >= 0.3 ? '#fff3e0' : '#e8f5e9',
                  color: maxRisk >= 0.45 ? '#c62828' : maxRisk >= 0.3 ? '#e65100' : '#2e7d32',
                  '& .MuiChip-icon': { ml: 0.5 },
                }}
              />
            </Tooltip>
          )}

          {/* History button */}
          <Tooltip title={t('scanner.header.historyTooltip')} arrow>
            <IconButton onClick={openHistory} sx={{
              bgcolor: '#f1f5f9', color: '#475569', border: '1px solid #e2e8f0',
              '&:hover': { bgcolor: '#e2e8f0' },
              width: 36, height: 36,
            }}>
              <HistoryIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </Tooltip>

          {/* Filter button */}
          <Badge badgeContent={filters.patterns.length} color="primary" max={99}
            sx={{ '& .MuiBadge-badge': { fontSize: '0.58rem', height: 18, minWidth: 18 } }}>
            <IconButton onClick={() => setDrawerOpen(true)} sx={{
              bgcolor: '#f1f5f9', color: '#475569', border: '1px solid #e2e8f0',
              '&:hover': { bgcolor: '#e2e8f0' },
              width: 36, height: 36,
            }}>
              <TuneIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </Badge>

          {/* Scan button */}
          <Button
            onClick={handleScan} disabled={loading || !filters.patterns.length}
            startIcon={loading ? <CircularProgress size={16} sx={{ color: '#fff' }} /> : <SearchIcon sx={{ fontSize: 18 }} />}
            sx={{
              textTransform: 'none', fontWeight: 700, fontSize: '0.82rem',
              px: 2.5, py: 0.9, borderRadius: 1,
              bgcolor: '#667eea',
              color: '#fff', boxShadow: 'none',
              '&:hover': { bgcolor: '#5568d3', boxShadow: 'none' },
              '&:disabled': { bgcolor: '#e2e8f0', color: '#94a3b8', boxShadow: 'none' },
            }}
          >
            {loading ? t('scanner.header.scanning') : t('scanner.header.scanBtn')}
          </Button>
        </Box>
      </Box>

      {/* ═══ RISK DETAIL PANEL ═══ */}
      {showRiskDetail && riskHorizons.length > 0 && (
        <Paper elevation={0} sx={{ mb: 2, p: 2, borderRadius: 2, border: '1px solid #f0f0f0', boxShadow: '0 1px 3px rgba(0,0,0,0.08)' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0 }}>
            {/* SMA indicators (unique info not in header) */}
            {marketStatus && (marketStatus.sma50 || marketStatus.sma200) && (
              <Box sx={{ display: 'flex', gap: 2.5, pr: 3, borderRight: '1px solid #f0f0f0', mr: 3 }}>
                {marketStatus.sma50 && (
                  <Box>
                    <Typography sx={{ fontSize: '0.55rem', fontWeight: 600, color: '#94a3b8', textTransform: 'uppercase' }}>SMA50</Typography>
                    <Typography sx={{ fontSize: '0.8rem', fontWeight: 700, color: marketStatus.vnindex_close >= marketStatus.sma50 ? '#2e7d32' : '#c62828', fontFamily: 'monospace' }}>
                      {marketStatus.sma50?.toLocaleString('vi-VN', { minimumFractionDigits: 0 })}
                    </Typography>
                  </Box>
                )}
                {marketStatus.sma200 && (
                  <Box>
                    <Typography sx={{ fontSize: '0.55rem', fontWeight: 600, color: '#94a3b8', textTransform: 'uppercase' }}>SMA200</Typography>
                    <Typography sx={{ fontSize: '0.8rem', fontWeight: 700, color: marketStatus.vnindex_close >= marketStatus.sma200 ? '#2e7d32' : '#c62828', fontFamily: 'monospace' }}>
                      {marketStatus.sma200?.toLocaleString('vi-VN', { minimumFractionDigits: 0 })}
                    </Typography>
                  </Box>
                )}
              </Box>
            )}

            {/* Risk Gauges */}
            <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mr: 1 }}>
                <ShieldIcon sx={{ fontSize: 18, color: maxRisk >= 0.45 ? '#c62828' : maxRisk >= 0.3 ? '#e65100' : '#2e7d32' }} />
                <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, color: '#475569', whiteSpace: 'nowrap' }}>{t('scanner.regime.riskAdjust')}</Typography>
              </Box>
              <Box sx={{ display: 'flex', gap: 3, flex: 1 }}>
                {riskHorizons.map(r => (
                  <RiskGauge key={r.h} probability={r.p} horizon={r.h} />
                ))}
              </Box>
            </Box>

            <IconButton size="small" onClick={() => setShowRiskDetail(false)} sx={{ color: '#94a3b8', alignSelf: 'flex-start' }}>
              <KeyboardArrowUpIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </Box>
        </Paper>
      )}

      {/* Error */}
      {error && (
        <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* ═══ STATS ROW ═══ */}
      {stats && !loading && (
        <Box sx={{ display: 'flex', gap: 2, mb: 2.5, flexWrap: 'wrap' }}>
          <StatCard label={t('scanner.stats.signals')} value={stats.total} color="#667eea" bg="#fafaff" sub={`/ ${scanResults?.total_scanned || 0}`} icon={<SignalCellularAltIcon />} />
          <StatCard label={t('scanner.stats.avgScore')} value={stats.avg.toFixed(1)} color={scoreColor(stats.avg)} bg="#fafffe" icon={<BarChartIcon />} />
          <StatCard label={t('scanner.stats.highScore')} value={stats.high} color="#e65100" bg="#fffcfa" icon={<BoltIcon />} />
          {stats.avgExp != null && (
            <StatCard label={t('scanner.stats.avgProfit')} value={`${stats.avgExp > 0 ? '+' : ''}${stats.avgExp.toFixed(1)}%`} color={stats.avgExp > 0 ? '#2e7d32' : '#c62828'} bg={stats.avgExp > 0 ? '#fafffa' : '#fffafa'} icon={<TrendingUpIcon />} />
          )}
        </Box>
      )}

      {/* ═══ TOOLBAR ═══ */}
      {sortedResults.length > 0 && (
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
          {/* Sort */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography sx={{ fontSize: '0.72rem', color: '#64748b', fontWeight: 600 }}>{t('scanner.sort.label')}</Typography>
            {[
              { key: 'score', label: t('scanner.sort.score') },
              { key: 'expectancy', label: t('scanner.sort.expectancy') },
              { key: 'volume', label: t('scanner.sort.volume') },
            ].map(s => (
              <Chip key={s.key} label={s.label} size="small"
                onClick={() => setSortBy(s.key)}
                sx={{
                  height: 26, fontSize: '0.68rem', fontWeight: 600, cursor: 'pointer',
                  bgcolor: sortBy === s.key ? '#667eea' : '#fff',
                  color: sortBy === s.key ? '#fff' : '#475569',
                  border: sortBy === s.key ? 'none' : '1px solid #e2e8f0',
                  '&:hover': { bgcolor: sortBy === s.key ? '#5568d3' : '#f8fafc' },
                }}
              />
            ))}
          </Box>
        </Box>
      )}

      {/* ═══ RESULTS ═══ */}
      {loading && !scanResults ? (
        <>
        {/* Skeleton stat cards */}
        <Box sx={{ display: 'flex', gap: 2, mb: 2.5, flexWrap: 'wrap',
          animation: 'pulse 1.5s infinite',
          '@keyframes pulse': { '0%, 100%': { opacity: 0.7 }, '50%': { opacity: 0.35 } },
        }}>
          {[0, 1, 2, 3].map(i => (
            <Box key={i} sx={{ flex: 1, minWidth: 140, p: 1.5, borderRadius: '6px', border: '1px solid #f0f0f0', bgcolor: '#fff', display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Box sx={{ width: 36, height: 36, borderRadius: '6px', bgcolor: '#f1f5f9' }} />
              <Box>
                <Box sx={{ width: 50, height: 9, bgcolor: '#e2e8f0', borderRadius: 1, mb: 0.6 }} />
                <Box sx={{ width: 36, height: 16, bgcolor: '#e2e8f0', borderRadius: 1 }} />
              </Box>
            </Box>
          ))}
        </Box>
        {/* Skeleton toolbar */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2,
          animation: 'pulse 1.5s infinite',
          '@keyframes pulse': { '0%, 100%': { opacity: 0.7 }, '50%': { opacity: 0.35 } },
        }}>
          <Box sx={{ width: 50, height: 11, bgcolor: '#e2e8f0', borderRadius: 1 }} />
          {[44, 52, 32].map((w, i) => (
            <Box key={i} sx={{ width: w, height: 26, bgcolor: i === 0 ? '#e8ecfc' : '#f1f5f9', borderRadius: '6px' }} />
          ))}
        </Box>
        {/* Skeleton table */}
        <Paper elevation={0} sx={{ borderRadius: 2, border: '1px solid #f0f0f0', overflow: 'hidden', boxShadow: '0 1px 3px rgba(0,0,0,0.08)' }}>
          {/* Skeleton header — same as real header */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, px: 2, py: 1, bgcolor: '#fafafa', borderBottom: '1px solid #f0f0f0' }}>
            <Box sx={{ width: 24 }}><Box sx={{ width: 10, height: 10, bgcolor: '#e2e8f0', borderRadius: 1 }} /></Box>
            <Box sx={{ width: 4 }} />
            <Box sx={{ flex: 1, minWidth: 120 }}><Box sx={{ width: 20, height: 10, bgcolor: '#e2e8f0', borderRadius: 1 }} /></Box>
            <Box sx={{ width: 120 }}><Box sx={{ width: 32, height: 10, bgcolor: '#e2e8f0', borderRadius: 1 }} /></Box>
            <Box sx={{ width: 160 }}><Box sx={{ width: 50, height: 10, bgcolor: '#e2e8f0', borderRadius: 1 }} /></Box>
            <Box sx={{ width: 40, textAlign: 'center' }}><Box sx={{ width: 28, height: 10, bgcolor: '#e2e8f0', borderRadius: 1, mx: 'auto' }} /></Box>
            <Box sx={{ width: 90, display: 'flex', gap: 1 }}>
              <Box sx={{ width: 20, height: 10, bgcolor: '#e2e8f0', borderRadius: 1 }} />
              <Box sx={{ width: 16, height: 10, bgcolor: '#e2e8f0', borderRadius: 1 }} />
            </Box>
            <Box sx={{ width: 56, display: 'flex', justifyContent: 'flex-end' }}><Box sx={{ width: 40, height: 10, bgcolor: '#e2e8f0', borderRadius: 1 }} /></Box>
            <Box sx={{ width: 70, display: 'flex', justifyContent: 'flex-end' }}><Box sx={{ width: 24, height: 10, bgcolor: '#e2e8f0', borderRadius: 1 }} /></Box>
            <Box sx={{ width: 70, display: 'flex', justifyContent: 'flex-end' }}><Box sx={{ width: 16, height: 10, bgcolor: '#e2e8f0', borderRadius: 1 }} /></Box>
            <Box sx={{ width: 70, display: 'flex', justifyContent: 'flex-end' }}><Box sx={{ width: 16, height: 10, bgcolor: '#e2e8f0', borderRadius: 1 }} /></Box>
            <Box sx={{ width: 56, display: 'flex', justifyContent: 'flex-end' }}><Box sx={{ width: 30, height: 10, bgcolor: '#e2e8f0', borderRadius: 1 }} /></Box>
          </Box>
          {/* Skeleton rows — mirror SignalRow layout */}
          {Array.from({ length: 6 }).map((_, i) => (
            <Box key={i} sx={{
              display: 'flex', alignItems: 'center', gap: 1.5, px: 2, py: 1, borderBottom: '1px solid #f0f0f0',
              animation: 'pulse 1.5s infinite',
              animationDelay: `${i * 0.1}s`,
              '@keyframes pulse': { '0%, 100%': { opacity: 0.7 }, '50%': { opacity: 0.35 } },
            }}>
              {/* # */}
              <Box sx={{ width: 24, textAlign: 'center' }}><Box sx={{ width: 14, height: 12, bgcolor: '#e2e8f0', borderRadius: 1, mx: 'auto' }} /></Box>
              {/* color bar */}
              <Box sx={{ width: 4, height: 28, bgcolor: '#e2e8f0', borderRadius: 2, flexShrink: 0 }} />
              {/* ticker + company */}
              <Box sx={{ flex: 1, minWidth: 120 }}>
                <Box sx={{ width: 52, height: 13, bgcolor: '#e2e8f0', borderRadius: 1, mb: 0.5 }} />
                <Box sx={{ width: 100 + (i % 3) * 20, height: 8, bgcolor: '#f0f0f0', borderRadius: 1 }} />
              </Box>
              {/* sector */}
              <Box sx={{ width: 120 }}><Box sx={{ width: 70 + (i % 3) * 15, height: 11, bgcolor: '#e2e8f0', borderRadius: 1 }} /></Box>
              {/* pattern */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, width: 160 }}>
                <Box sx={{ width: 14, height: 14, bgcolor: '#e2e8f0', borderRadius: 0.5, flexShrink: 0 }} />
                <Box sx={{ width: 80 + (i % 2) * 20, height: 11, bgcolor: '#e2e8f0', borderRadius: 1 }} />
              </Box>
              {/* score */}
              <Box sx={{ width: 40, textAlign: 'center' }}><Box sx={{ width: 28, height: 16, bgcolor: '#e2e8f0', borderRadius: 1, mx: 'auto' }} /></Box>
              {/* MH + KL */}
              <Box sx={{ display: 'flex', gap: 1, width: 90 }}>
                <Box sx={{ width: 28, height: 11, bgcolor: '#e2e8f0', borderRadius: 1 }} />
                <Box sx={{ width: 28, height: 11, bgcolor: '#e2e8f0', borderRadius: 1 }} />
              </Box>
              {/* expectancy */}
              <Box sx={{ width: 56, display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
                <Box sx={{ width: 38, height: 12, bgcolor: '#e2e8f0', borderRadius: 1, mb: 0.3 }} />
                <Box sx={{ width: 26, height: 8, bgcolor: '#f0f0f0', borderRadius: 1 }} />
              </Box>
              {/* price */}
              <Box sx={{ width: 70, display: 'flex', justifyContent: 'flex-end' }}><Box sx={{ width: 48, height: 13, bgcolor: '#e2e8f0', borderRadius: 1 }} /></Box>
              {/* SL */}
              <Box sx={{ width: 70, display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
                <Box sx={{ width: 44, height: 11, bgcolor: '#fce4e4', borderRadius: 1, mb: 0.3 }} />
                <Box sx={{ width: 28, height: 8, bgcolor: '#fce4e4', borderRadius: 1 }} />
              </Box>
              {/* TP */}
              <Box sx={{ width: 70, display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
                <Box sx={{ width: 44, height: 11, bgcolor: '#e8f5e9', borderRadius: 1, mb: 0.3 }} />
                <Box sx={{ width: 28, height: 8, bgcolor: '#e8f5e9', borderRadius: 1 }} />
              </Box>
              {/* volume */}
              <Box sx={{ width: 56, display: 'flex', justifyContent: 'flex-end' }}><Box sx={{ width: 36, height: 11, bgcolor: '#e2e8f0', borderRadius: 1 }} /></Box>
            </Box>
          ))}
        </Paper>
        </>
      ) : sortedResults.length > 0 ? (
          <Paper elevation={0} sx={{ borderRadius: 2, border: '1px solid #f0f0f0', overflow: 'hidden', boxShadow: '0 1px 3px rgba(0,0,0,0.08)' }}>
            {/* Header */}
            <Box sx={{
              display: 'flex', alignItems: 'center', gap: 1.5, px: 2, py: 1,
              bgcolor: '#fafafa', borderBottom: '1px solid #f0f0f0',
            }}>
              <Typography sx={{ fontSize: '0.62rem', fontWeight: 700, color: '#94a3b8', width: 24, textAlign: 'center', textTransform: 'uppercase' }}>#</Typography>
              <Box sx={{ width: 4, flexShrink: 0 }} />
              <Typography sx={{ fontSize: '0.62rem', fontWeight: 700, color: '#94a3b8', flex: 1, minWidth: 120, textTransform: 'uppercase' }}>{t('scanner.table.code')}</Typography>
              <Typography sx={{ fontSize: '0.62rem', fontWeight: 700, color: '#94a3b8', width: 120, textTransform: 'uppercase' }}>{t('scanner.table.sector')}</Typography>
              <Typography sx={{ fontSize: '0.62rem', fontWeight: 700, color: '#94a3b8', width: 160, textTransform: 'uppercase' }}>{t('scanner.table.pattern')}</Typography>
              <Typography sx={{ fontSize: '0.62rem', fontWeight: 700, color: '#94a3b8', width: 40, textAlign: 'center', textTransform: 'uppercase' }}>{t('scanner.table.score')}</Typography>
              <Typography sx={{ fontSize: '0.62rem', fontWeight: 700, color: '#94a3b8', width: 28, textAlign: 'center', textTransform: 'uppercase' }}>{t('scanner.table.confidence')}</Typography>
              <Typography sx={{ fontSize: '0.62rem', fontWeight: 700, color: '#94a3b8', minWidth: 96, textAlign: 'center', textTransform: 'uppercase' }}>{t('scanner.table.aiConf', 'AI')}</Typography>
              <Box sx={{ width: 90, display: 'flex', gap: 1 }}>
                <Typography sx={{ fontSize: '0.62rem', fontWeight: 700, color: '#94a3b8', width: 32, textTransform: 'uppercase' }}>{t('scanner.table.patternShort')}</Typography>
                <Typography sx={{ fontSize: '0.62rem', fontWeight: 700, color: '#94a3b8', width: 32, textTransform: 'uppercase' }}>{t('scanner.table.volumeShort')}</Typography>
              </Box>
              <Typography sx={{ fontSize: '0.62rem', fontWeight: 700, color: '#94a3b8', width: 56, textAlign: 'right', textTransform: 'uppercase' }}>{t('scanner.table.profitPerTrade')}</Typography>
              <Typography sx={{ fontSize: '0.62rem', fontWeight: 700, color: '#94a3b8', width: 70, textAlign: 'right', textTransform: 'uppercase' }}>{t('scanner.table.price')}</Typography>
              <Typography sx={{ fontSize: '0.62rem', fontWeight: 700, color: '#c62828', width: 70, textAlign: 'right', textTransform: 'uppercase' }}>SL</Typography>
              <Typography sx={{ fontSize: '0.62rem', fontWeight: 700, color: '#2e7d32', width: 70, textAlign: 'right', textTransform: 'uppercase' }}>TP</Typography>
              <Typography sx={{ fontSize: '0.62rem', fontWeight: 700, color: '#94a3b8', width: 56, textAlign: 'right', textTransform: 'uppercase' }}>{t('scanner.table.tradingVol')}</Typography>
            </Box>
            {sortedResults.map((r, i) => (
              <SignalRow key={r.ticker} data={r} index={i} t={t} language={i18n.language}
                onClick={() => navigate(`/stocks/${r.ticker}`, { state: { from: '/scanner' } })}
              />
            ))}
          </Paper>
      ) : scanResults ? (
        <Paper elevation={0} sx={{ py: 8, textAlign: 'center', borderRadius: 2, border: '1px solid #f0f0f0' }}>
          <BarChartIcon sx={{ fontSize: 48, color: '#cbd5e1', mb: 1 }} />
          <Typography sx={{ fontSize: '0.95rem', color: '#475569', fontWeight: 700 }}>
            {t('scanner.noSignals.title')}
          </Typography>
          <Typography sx={{ fontSize: '0.75rem', color: '#94a3b8', mt: 0.5 }}>
            {t('scanner.noSignals.subtitle', { count: scanResults.total_scanned || 0 })}
          </Typography>
        </Paper>
      ) : null}

      {/* ═══ FILTER DRAWER ═══ */}
      <Drawer
        anchor="right"
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        PaperProps={{
          sx: { width: { xs: '100%', sm: 480 }, bgcolor: '#fff' },
        }}
      >
        <Box sx={{
          px: 2.5, py: 2,
          borderBottom: '1px solid #e2e8f0',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <TuneIcon sx={{ fontSize: 20, color: '#667eea' }} />
            <Typography sx={{ fontWeight: 800, fontSize: '1.05rem', color: '#1e293b' }}>{t('scanner.header.filterTitle')}</Typography>
          </Box>
          <IconButton onClick={() => setDrawerOpen(false)} sx={{ color: '#94a3b8' }}>
            <CloseIcon sx={{ fontSize: 20 }} />
          </IconButton>
        </Box>
        <Box sx={{ p: 2.5, overflow: 'auto', flex: 1 }}>
          <ScannerFilters filters={filters} onChange={setFilters} loading={loading} patternPerformance={patternPerformance} />
        </Box>
        <Box sx={{ p: 2.5, borderTop: '1px solid #e2e8f0' }}>
          <Button fullWidth variant="contained" onClick={() => { setDrawerOpen(false); handleScan(); }}
            disabled={loading || !filters.patterns.length}
            startIcon={loading ? <CircularProgress size={18} sx={{ color: '#fff' }} /> : <SearchIcon />}
            sx={{
              textTransform: 'none', fontWeight: 700, fontSize: '0.95rem', py: 1.5, borderRadius: '8px',
              bgcolor: '#667eea', boxShadow: 'none',
              '&:hover': { bgcolor: '#5568d3', boxShadow: 'none' },
              '&:disabled': { bgcolor: '#cbd5e1', color: '#fff' },
            }}
          >
            {loading ? t('scanner.header.scanning') : t('scanner.header.scanWithCount', { count: filters.patterns.length })}
          </Button>
        </Box>
      </Drawer>

      {/* ═══ HISTORY DRAWER ═══ */}
      <Drawer
        anchor="right"
        open={historyOpen}
        onClose={() => { setHistoryOpen(false); setSelectedSession(null); }}
        PaperProps={{ sx: { width: { xs: '100%', sm: 440 }, bgcolor: '#f8fafc', display: 'flex', flexDirection: 'column' } }}
      >
        {/* ── Header + tabs ── */}
        <Box sx={{ bgcolor: '#fff', borderBottom: '1px solid #e2e8f0', px: 2, pt: 1.5, pb: 0 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
            <Typography sx={{ fontWeight: 800, fontSize: '0.9rem', color: '#1e293b', display: 'flex', alignItems: 'center', gap: 0.75 }}>
              <HistoryIcon sx={{ fontSize: 17, color: '#667eea' }} /> {t('scanner.history.title')}
            </Typography>
            <IconButton onClick={() => { setHistoryOpen(false); setSelectedSession(null); }} size="small" sx={{ color: '#94a3b8', p: 0.5 }}>
              <CloseIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </Box>
          <Box sx={{ display: 'flex', gap: 0 }}>
            {[
              { key: 'overview', label: t('scanner.history.tabOverview') },
              { key: 'sessions', label: t('scanner.history.tabSessions') },
              { key: 'patterns', label: t('scanner.history.tabPatterns') },
            ].map(tb => (
              <Box key={tb.key}
                onClick={() => { setHistoryTab(tb.key); setSelectedSession(null); }}
                sx={{
                  px: 1.5, py: 0.8, cursor: 'pointer',
                  borderBottom: historyTab === tb.key ? '2px solid #667eea' : '2px solid transparent',
                  transition: 'all 0.15s',
                }}>
                <Typography sx={{
                  fontSize: '0.72rem', fontWeight: historyTab === tb.key ? 700 : 500,
                  color: historyTab === tb.key ? '#667eea' : '#94a3b8',
                }}>
                  {tb.label}
                </Typography>
              </Box>
            ))}
          </Box>
        </Box>

        {/* ── Content ── */}
        <Box sx={{ overflow: 'auto', flex: 1, px: 1.5, py: 1.5 }}>
          {historyLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
              <CircularProgress size={24} sx={{ color: '#667eea' }} />
            </Box>
          ) : historyTab === 'overview' ? (
            /* ═══ OVERVIEW TAB ═══ */
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
              {!historySummary ? (
                <Typography sx={{ fontSize: '0.78rem', color: '#94a3b8', textAlign: 'center', py: 6 }}>Chưa có dữ liệu</Typography>
              ) : (() => {
                const o = historySummary.overall || {};
                const fwd = historySummary.forward_returns || {};
                const resolved = (o.total_wins || 0) + (o.total_losses || 0) + (o.total_expired || 0);
                return (
                  <>
                    {/* KPI Cards */}
                    <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 0.75 }}>
                      {[
                        { label: t('scanner.history.totalSignals'), value: o.total_signals || 0, color: '#667eea' },
                        { label: t('scanner.history.winRate'), value: o.win_rate != null ? `${o.win_rate}%` : '-', color: (o.win_rate || 0) >= 50 ? '#10b981' : '#ef4444' },
                        { label: t('scanner.history.profitFactor'), value: o.profit_factor || '-', color: (o.profit_factor || 0) >= 1 ? '#10b981' : '#ef4444' },
                      ].map((kpi, i) => (
                        <Box key={i} sx={{ bgcolor: '#fff', borderRadius: '8px', p: 1, textAlign: 'center', border: '1px solid #f0f0f0' }}>
                          <Typography sx={{ fontSize: '1rem', fontWeight: 900, color: kpi.color, fontFamily: 'monospace' }}>{kpi.value}</Typography>
                          <Typography sx={{ fontSize: '0.55rem', color: '#94a3b8', fontWeight: 600, mt: 0.25 }}>{kpi.label}</Typography>
                        </Box>
                      ))}
                    </Box>

                    {/* W/L Breakdown */}
                    <Box sx={{ bgcolor: '#fff', borderRadius: '8px', p: 1.25, border: '1px solid #f0f0f0' }}>
                      <Typography sx={{ fontSize: '0.65rem', fontWeight: 700, color: '#475569', mb: 0.75 }}>{t('scanner.history.resultBreakdown')}</Typography>
                      {resolved > 0 && (
                        <Box sx={{ display: 'flex', height: 6, borderRadius: 3, overflow: 'hidden', mb: 0.75 }}>
                          <Box sx={{ width: `${(o.total_wins / resolved) * 100}%`, bgcolor: '#10b981' }} />
                          <Box sx={{ width: `${(o.total_losses / resolved) * 100}%`, bgcolor: '#ef4444' }} />
                          <Box sx={{ width: `${(o.total_expired / resolved) * 100}%`, bgcolor: '#94a3b8' }} />
                        </Box>
                      )}
                      <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
                        {[
                          { label: t('scanner.history.win'), val: o.total_wins, color: '#10b981', ret: o.avg_win_return },
                          { label: t('scanner.history.loss'), val: o.total_losses, color: '#ef4444', ret: o.avg_loss_return },
                          { label: t('scanner.history.expired'), val: o.total_expired, color: '#94a3b8' },
                          { label: t('scanner.history.pending'), val: o.total_pending, color: '#667eea' },
                        ].map((item, i) => (
                          <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: item.color }} />
                            <Typography sx={{ fontSize: '0.6rem', color: '#475569' }}>
                              <b>{item.val || 0}</b> {item.label}
                              {item.ret != null && <span style={{ color: item.color, fontFamily: 'monospace', fontWeight: 700, marginLeft: 4 }}>{t('scanner.history.avg')} {item.ret > 0 ? '+' : ''}{item.ret}%</span>}
                            </Typography>
                          </Box>
                        ))}
                      </Box>
                    </Box>

                    {/* Forward Returns */}
                    <Box sx={{ bgcolor: '#fff', borderRadius: '8px', p: 1.25, border: '1px solid #f0f0f0' }}>
                      <Typography sx={{ fontSize: '0.65rem', fontWeight: 700, color: '#475569', mb: 0.75 }}>{t('scanner.history.forwardReturns')}</Typography>
                      <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: 0.5 }}>
                        {[
                          { label: t('scanner.history.after1d'), val: fwd.avg_1d_return },
                          { label: t('scanner.history.after3d'), val: fwd.avg_3d_return },
                          { label: t('scanner.history.after5d'), val: fwd.avg_5d_return },
                          { label: t('scanner.history.after10d'), val: fwd.avg_10d_return },
                        ].map((f, i) => (
                          <Box key={i} sx={{ textAlign: 'center', py: 0.5 }}>
                            <Typography sx={{
                              fontSize: '0.85rem', fontWeight: 900, fontFamily: 'monospace',
                              color: f.val > 0 ? '#10b981' : f.val < 0 ? '#ef4444' : '#94a3b8',
                            }}>
                              {f.val != null ? `${f.val > 0 ? '+' : ''}${f.val}%` : '-'}
                            </Typography>
                            <Typography sx={{ fontSize: '0.5rem', color: '#94a3b8', fontWeight: 600 }}>{f.label}</Typography>
                          </Box>
                        ))}
                      </Box>
                    </Box>

                    {/* Score → Outcome Insight */}
                    {historySummary.score_insights?.length > 0 && (
                      <Box sx={{ bgcolor: '#fff', borderRadius: '8px', p: 1.25, border: '1px solid #f0f0f0' }}>
                        <Typography sx={{ fontSize: '0.65rem', fontWeight: 700, color: '#475569', mb: 0.75 }}>{t('scanner.history.scoreToOutcome')}</Typography>
                        {historySummary.score_insights.map((si, i) => (
                          <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.4 }}>
                            <Typography sx={{ fontSize: '0.6rem', fontWeight: 700, color: '#475569', width: 72, flexShrink: 0 }}>{si.score_range}</Typography>
                            <Box sx={{ flex: 1, height: 5, borderRadius: 3, bgcolor: '#f0f0f0', overflow: 'hidden' }}>
                              <Box sx={{ width: `${si.win_rate || 0}%`, height: '100%', borderRadius: 3, bgcolor: (si.win_rate || 0) >= 50 ? '#10b981' : '#f59e0b' }} />
                            </Box>
                            <Typography sx={{ fontSize: '0.58rem', fontWeight: 800, fontFamily: 'monospace', color: (si.win_rate || 0) >= 50 ? '#10b981' : '#f59e0b', width: 36, textAlign: 'right' }}>
                              {si.win_rate != null ? `${si.win_rate}%` : '-'}
                            </Typography>
                            <Typography sx={{ fontSize: '0.5rem', color: '#94a3b8', width: 20, textAlign: 'right' }}>n={si.total}</Typography>
                          </Box>
                        ))}
                        <Typography sx={{ fontSize: '0.5rem', color: '#b0b8c4', mt: 0.5, fontStyle: 'italic' }}>
                          {t('scanner.history.scoreInsight')}
                        </Typography>
                      </Box>
                    )}

                    {/* Sector Performance */}
                    {historySummary.sector_performance?.length > 0 && (
                      <Box sx={{ bgcolor: '#fff', borderRadius: '8px', p: 1.25, border: '1px solid #f0f0f0' }}>
                        <Typography sx={{ fontSize: '0.65rem', fontWeight: 700, color: '#475569', mb: 0.75 }}>{t('scanner.history.sectorPerformance')}</Typography>
                        {historySummary.sector_performance.slice(0, 8).map((sp, i) => (
                          <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 0.75, py: 0.35 }}>
                            <Typography sx={{ fontSize: '0.6rem', fontWeight: 600, color: '#475569', width: 56, flexShrink: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              {sp.sector || '?'}
                            </Typography>
                            <Box sx={{ flex: 1, height: 5, borderRadius: 3, bgcolor: '#f0f0f0', overflow: 'hidden' }}>
                              <Box sx={{ width: `${sp.win_rate || 0}%`, height: '100%', borderRadius: 3, bgcolor: (sp.win_rate || 0) >= 50 ? '#10b981' : '#ef4444' }} />
                            </Box>
                            <Typography sx={{ fontSize: '0.55rem', fontWeight: 800, color: (sp.win_rate || 0) >= 50 ? '#10b981' : '#ef4444', fontFamily: 'monospace', width: 32, textAlign: 'right' }}>
                              {sp.win_rate != null ? `${sp.win_rate}%` : '-'}
                            </Typography>
                            <Typography sx={{ fontSize: '0.5rem', color: '#94a3b8', width: 30, textAlign: 'right' }}>{sp.wins}W/{sp.losses}L</Typography>
                          </Box>
                        ))}
                      </Box>
                    )}

                    {/* Top Tickers */}
                    {historySummary.top_tickers?.length > 0 && (
                      <Box sx={{ bgcolor: '#fff', borderRadius: '8px', p: 1.25, border: '1px solid #f0f0f0' }}>
                        <Typography sx={{ fontSize: '0.65rem', fontWeight: 700, color: '#475569', mb: 0.75 }}>{t('scanner.history.topTickers')}</Typography>
                        {historySummary.top_tickers.map((tk, i) => (
                          <Box key={i} onClick={() => navigate(`/stocks/${tk.ticker}`, { state: { from: '/scanner' } })}
                            sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.4, cursor: 'pointer', '&:hover': { bgcolor: '#f8fafc' }, borderRadius: '4px', px: 0.5 }}>
                            <Typography sx={{ fontSize: '0.55rem', fontWeight: 700, color: '#94a3b8', width: 14 }}>#{i + 1}</Typography>
                            <Typography sx={{ fontSize: '0.7rem', fontWeight: 800, color: '#1e293b', width: 50 }}>{tk.ticker}</Typography>
                            <Typography sx={{ fontSize: '0.6rem', color: '#94a3b8' }}>{tk.signals} {t('scanner.history.times')} · {tk.wins}W</Typography>
                            <Box sx={{ flex: 1 }} />
                            <Typography sx={{ fontSize: '0.7rem', fontWeight: 900, fontFamily: 'monospace', color: (tk.avg_return || 0) >= 0 ? '#10b981' : '#ef4444' }}>
                              {tk.avg_return > 0 ? '+' : ''}{tk.avg_return}%
                            </Typography>
                          </Box>
                        ))}
                      </Box>
                    )}

                    {/* Summary stats */}
                    <Box sx={{ display: 'flex', gap: 0.75 }}>
                      <Box sx={{ flex: 1, bgcolor: '#fff', borderRadius: '8px', p: 1, textAlign: 'center', border: '1px solid #f0f0f0' }}>
                        <Typography sx={{ fontSize: '0.85rem', fontWeight: 900, color: '#667eea', fontFamily: 'monospace' }}>{o.total_sessions || 0}</Typography>
                        <Typography sx={{ fontSize: '0.5rem', color: '#94a3b8', fontWeight: 600 }}>{t('scanner.history.sessions')}</Typography>
                      </Box>
                      <Box sx={{ flex: 1, bgcolor: '#fff', borderRadius: '8px', p: 1, textAlign: 'center', border: '1px solid #f0f0f0' }}>
                        <Typography sx={{ fontSize: '0.85rem', fontWeight: 900, color: '#667eea', fontFamily: 'monospace' }}>{o.unique_tickers || 0}</Typography>
                        <Typography sx={{ fontSize: '0.5rem', color: '#94a3b8', fontWeight: 600 }}>{t('scanner.history.trackedTickers')}</Typography>
                      </Box>
                      <Box sx={{ flex: 1, bgcolor: '#fff', borderRadius: '8px', p: 1, textAlign: 'center', border: '1px solid #f0f0f0' }}>
                        <Typography sx={{ fontSize: '0.85rem', fontWeight: 900, fontFamily: 'monospace', color: (o.avg_return || 0) >= 0 ? '#10b981' : '#ef4444' }}>
                          {o.avg_return != null ? `${o.avg_return > 0 ? '+' : ''}${o.avg_return}%` : '-'}
                        </Typography>
                        <Typography sx={{ fontSize: '0.5rem', color: '#94a3b8', fontWeight: 600 }}>{t('scanner.history.avgReturn')}</Typography>
                      </Box>
                    </Box>
                  </>
                );
              })()}
            </Box>
          ) : historyTab === 'sessions' ? (
            selectedSession ? (
              /* ═══ Session detail ═══ */
              <Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, px: 0.5 }}>
                  <Typography onClick={() => setSelectedSession(null)}
                    sx={{ fontSize: '0.7rem', fontWeight: 600, color: '#667eea', cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }}>
                    ← {t('scanner.history.back').replace('← ', '')}
                  </Typography>
                  <Typography sx={{ fontSize: '0.65rem', color: '#94a3b8' }}>
                    {new Date(selectedSession.scan_date).toLocaleDateString('vi-VN')} · {selectedSession.total_signals} {t('scanner.history.signals')}
                  </Typography>
                </Box>

                {/* Session summary bar */}
                {(() => {
                  const wins = sessionSignals.filter(s => s.outcome === 'WIN').length;
                  const losses = sessionSignals.filter(s => s.outcome === 'LOSS').length;
                  const expired = sessionSignals.filter(s => s.outcome === 'EXPIRED').length;
                  const resolved = wins + losses + expired;
                  const wr = resolved > 0 ? Math.round(wins / resolved * 100) : null;
                  return resolved > 0 ? (
                    <Box sx={{ bgcolor: '#fff', borderRadius: '6px', p: 1, mb: 1, display: 'flex', alignItems: 'center', gap: 1.5, border: '1px solid #f0f0f0' }}>
                      <Box sx={{ display: 'flex', height: 5, flex: 1, borderRadius: 3, overflow: 'hidden' }}>
                        <Box sx={{ width: `${(wins / resolved) * 100}%`, bgcolor: '#10b981' }} />
                        <Box sx={{ width: `${(losses / resolved) * 100}%`, bgcolor: '#ef4444' }} />
                        <Box sx={{ width: `${(expired / resolved) * 100}%`, bgcolor: '#94a3b8' }} />
                      </Box>
                      <Typography sx={{ fontSize: '0.6rem', fontWeight: 800, fontFamily: 'monospace', color: wr >= 50 ? '#10b981' : '#ef4444' }}>
                        {wins}W {losses}L · {wr}%
                      </Typography>
                    </Box>
                  ) : null;
                })()}

                {/* Signal cards */}
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                  {sessionSignals.map((sig, i) => {
                    const sc = sig.combined_score || 0;
                    const scColor = sc >= 7 ? '#10b981' : sc >= 5 ? '#667eea' : '#f59e0b';
                    const entry = sig.entry_price || sig.close_price;
                    const riskPct = entry && sig.stop_loss ? ((entry - sig.stop_loss) / entry * 100).toFixed(1) : null;
                    const rrRatio = entry && sig.stop_loss && sig.target_price ? ((sig.target_price - entry) / (entry - sig.stop_loss)).toFixed(1) : null;
                    const outcomeColor = sig.outcome === 'WIN' ? '#10b981' : sig.outcome === 'EXPIRED' ? '#94a3b8' : '#ef4444';
                    return (
                      <Box key={i}
                        onClick={() => navigate(`/stocks/${sig.ticker}`, { state: { from: '/scanner' } })}
                        sx={{
                          bgcolor: '#fff', borderRadius: '8px', cursor: 'pointer',
                          border: '1px solid #f0f0f0',
                          '&:hover': { borderColor: '#e2e8f0', boxShadow: '0 1px 4px rgba(0,0,0,0.04)' },
                          overflow: 'hidden',
                        }}>
                        {/* Main row */}
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 1, py: 0.75 }}>
                          <Box sx={{
                            width: 32, height: 32, borderRadius: '6px', display: 'flex', alignItems: 'center', justifyContent: 'center',
                            bgcolor: alpha(scColor, 0.1), flexShrink: 0,
                          }}>
                            <Typography sx={{ fontWeight: 900, fontSize: '0.72rem', color: scColor, fontFamily: 'monospace' }}>
                              {sc.toFixed(1)}
                            </Typography>
                          </Box>
                          <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              <Typography sx={{ fontWeight: 800, fontSize: '0.78rem', color: '#1e293b' }}>{sig.ticker}</Typography>
                              <Typography sx={{ fontSize: '0.55rem', color: '#94a3b8', fontWeight: 500 }}>{getPatternLabel(sig.signal_type)}</Typography>
                            </Box>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mt: 0.15 }}>
                              <Typography sx={{ fontSize: '0.55rem', color: '#64748b' }}>
                                Entry {entry?.toLocaleString('vi-VN')}
                              </Typography>
                              {sig.stop_loss && (
                                <Typography sx={{ fontSize: '0.55rem', color: '#ef4444' }}>
                                  SL {Math.round(sig.stop_loss).toLocaleString('vi-VN')}
                                </Typography>
                              )}
                              {sig.target_price && (
                                <Typography sx={{ fontSize: '0.55rem', color: '#10b981' }}>
                                  TP {Math.round(sig.target_price).toLocaleString('vi-VN')}
                                </Typography>
                              )}
                            </Box>
                          </Box>
                          <Box sx={{ flexShrink: 0, textAlign: 'right' }}>
                            {sig.outcome ? (
                              <Box sx={{
                                px: 0.8, py: 0.25, borderRadius: '4px',
                                bgcolor: sig.outcome === 'WIN' ? '#ecfdf5' : sig.outcome === 'EXPIRED' ? '#f8fafc' : '#fef2f2',
                              }}>
                                <Typography sx={{
                                  fontSize: '0.62rem', fontWeight: 800, fontFamily: 'monospace', color: outcomeColor,
                                }}>
                                  {sig.outcome === 'EXPIRED' ? t('scanner.history.expired') : `${sig.outcome_pct > 0 ? '+' : ''}${sig.outcome_pct?.toFixed(1)}%`}
                                </Typography>
                              </Box>
                            ) : (
                              <HourglassEmptyIcon sx={{ fontSize: 14, color: '#cbd5e1' }} />
                            )}
                            {riskPct && (
                              <Typography sx={{ fontSize: '0.48rem', color: '#94a3b8', fontFamily: 'monospace', mt: 0.2 }}>
                                Risk {riskPct}% · R:R {rrRatio || '-'}
                              </Typography>
                            )}
                          </Box>
                        </Box>
                        {/* Forward returns row (if available) */}
                        {(sig.price_after_1d || sig.price_after_3d || sig.price_after_5d || sig.price_after_10d) && (
                          <Box sx={{ display: 'flex', borderTop: '1px solid #f5f5f5', px: 1, py: 0.4, gap: 0.5 }}>
                            {[
                              { label: '1D', price: sig.price_after_1d },
                              { label: '3D', price: sig.price_after_3d },
                              { label: '5D', price: sig.price_after_5d },
                              { label: '10D', price: sig.price_after_10d },
                            ].map((fw, fi) => {
                              const pct = fw.price && sig.close_price ? ((fw.price - sig.close_price) / sig.close_price * 100) : null;
                              return (
                                <Box key={fi} sx={{ flex: 1, textAlign: 'center' }}>
                                  <Typography sx={{
                                    fontSize: '0.58rem', fontWeight: 800, fontFamily: 'monospace',
                                    color: pct > 0 ? '#10b981' : pct < 0 ? '#ef4444' : '#94a3b8',
                                  }}>
                                    {pct != null ? `${pct > 0 ? '+' : ''}${pct.toFixed(1)}%` : '-'}
                                  </Typography>
                                  <Typography sx={{ fontSize: '0.42rem', color: '#b0b8c4' }}>{fw.label}</Typography>
                                </Box>
                              );
                            })}
                          </Box>
                        )}
                      </Box>
                    );
                  })}
                  {sessionSignals.length === 0 && (
                    <Typography sx={{ fontSize: '0.78rem', color: '#94a3b8', textAlign: 'center', py: 4 }}>{t('scanner.history.noSignals')}</Typography>
                  )}
                </Box>
              </Box>
            ) : (
              /* ═══ Sessions list ═══ */
              <Box>
                {historySessions.length === 0 ? (
                  <Typography sx={{ fontSize: '0.78rem', color: '#94a3b8', textAlign: 'center', py: 6 }}>{t('scanner.history.noHistory')}</Typography>
                ) : (() => {
                  const grouped = {};
                  historySessions.forEach(s => {
                    const d = new Date(s.scan_date);
                    const key = d.toLocaleDateString('vi-VN', { day: '2-digit', month: '2-digit', year: 'numeric' });
                    if (!grouped[key]) grouped[key] = { label: key, weekday: d.toLocaleDateString('vi-VN', { weekday: 'short' }), sessions: [] };
                    grouped[key].sessions.push(s);
                  });
                  return Object.values(grouped).map((group, gi) => (
                    <Box key={gi} sx={{ mb: 1.5 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.75, px: 0.5 }}>
                        <Typography sx={{ fontWeight: 700, fontSize: '0.65rem', color: '#475569', textTransform: 'capitalize' }}>
                          {group.weekday}, {group.label}
                        </Typography>
                        <Box sx={{ flex: 1, height: 1, bgcolor: '#e2e8f0' }} />
                      </Box>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                        {group.sessions.map(s => {
                          const regime = REGIME_MAP[s.market_regime] || {};
                          const hasOutcome = s.resolved > 0;
                          const winRate = hasOutcome ? Math.round(s.wins / s.resolved * 100) : null;
                          return (
                            <Box key={s.id} onClick={() => viewSession(s.id)}
                              sx={{
                                display: 'flex', alignItems: 'center', gap: 1.25, py: 0.75, px: 1,
                                bgcolor: '#fff', borderRadius: '6px', cursor: 'pointer',
                                border: '1px solid transparent',
                                transition: 'all 0.12s',
                                '&:hover': { borderColor: '#e2e8f0', boxShadow: '0 1px 4px rgba(0,0,0,0.05)' },
                              }}>
                              <Box sx={{
                                width: 36, height: 36, borderRadius: '8px', flexShrink: 0,
                                display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
                                bgcolor: hasOutcome
                                  ? alpha(s.wins >= s.losses ? '#10b981' : '#ef4444', 0.08)
                                  : alpha('#667eea', 0.08),
                              }}>
                                <Typography sx={{
                                  fontWeight: 900, fontSize: '0.88rem', lineHeight: 1,
                                  fontFamily: 'ui-monospace, monospace',
                                  color: hasOutcome ? (s.wins >= s.losses ? '#10b981' : '#ef4444') : '#667eea',
                                }}>
                                  {s.total_signals}
                                </Typography>
                              </Box>
                              <Box sx={{ flex: 1, minWidth: 0 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                                  {s.market_regime && (
                                    <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.25 }}>
                                      <Box sx={{ width: 5, height: 5, borderRadius: '50%', bgcolor: regime.color || '#94a3b8' }} />
                                      <Typography sx={{ fontSize: '0.55rem', fontWeight: 600, color: regime.color || '#94a3b8' }}>
                                        {regime.labelKey ? t(regime.labelKey) : (s.market_regime || '')}
                                      </Typography>
                                    </Box>
                                  )}
                                  {s.vnindex_close && (
                                    <Typography sx={{ fontSize: '0.52rem', color: '#94a3b8', fontFamily: 'monospace' }}>
                                      VNI {Math.round(s.vnindex_close).toLocaleString('vi-VN')}
                                    </Typography>
                                  )}
                                </Box>
                                <Typography sx={{ fontSize: '0.55rem', color: '#94a3b8', mt: 0.1 }}>
                                  {s.total_scanned} {t('scanner.history.stocks')} · {t('scanner.history.avg')} {s.avg_score?.toFixed(1) || '-'}đ
                                  {s.avg_expectancy != null && <span style={{ fontFamily: 'monospace', marginLeft: 4 }}>EV {s.avg_expectancy > 0 ? '+' : ''}{s.avg_expectancy?.toFixed(1)}%</span>}
                                </Typography>
                              </Box>
                              <Box sx={{ flexShrink: 0, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                {hasOutcome ? (
                                  <>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                      <Typography sx={{ fontSize: '0.62rem', fontWeight: 800, color: '#10b981' }}>{s.wins}W</Typography>
                                      <Typography sx={{ fontSize: '0.62rem', fontWeight: 800, color: '#ef4444' }}>{s.losses}L</Typography>
                                    </Box>
                                    {winRate != null && (
                                      <Box sx={{
                                        width: 32, height: 32, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center',
                                        border: `2px solid ${winRate >= 50 ? '#10b981' : '#ef4444'}`,
                                      }}>
                                        <Typography sx={{ fontSize: '0.55rem', fontWeight: 900, color: winRate >= 50 ? '#10b981' : '#ef4444', fontFamily: 'monospace' }}>
                                          {winRate}%
                                        </Typography>
                                      </Box>
                                    )}
                                  </>
                                ) : (
                                  <Typography sx={{ fontSize: '0.62rem', color: '#667eea', fontWeight: 700, fontFamily: 'monospace' }}>
                                    {s.avg_score?.toFixed(1) || '-'}đ
                                  </Typography>
                                )}
                                <KeyboardArrowUpIcon sx={{ fontSize: 14, color: '#cbd5e1', transform: 'rotate(90deg)' }} />
                              </Box>
                            </Box>
                          );
                        })}
                      </Box>
                    </Box>
                  ));
                })()}
              </Box>
            )
          ) : (
            /* ═══ Pattern accuracy tab ═══ */
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
              {patternAccuracy.length === 0 ? (
                <Typography sx={{ fontSize: '0.78rem', color: '#94a3b8', textAlign: 'center', py: 6 }}>{t('scanner.history.noData')}</Typography>
              ) : patternAccuracy.map((p, i) => {
                const wr = p.win_rate || 0;
                const wrColor = wr >= 60 ? '#10b981' : wr >= 40 ? '#f59e0b' : '#ef4444';
                const resolved = (p.wins || 0) + (p.losses || 0);
                return (
                  <Box key={i} sx={{ bgcolor: '#fff', borderRadius: '8px', p: 1.25, border: '1px solid #f0f0f0' }}>
                    {/* Header row */}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.5 }}>
                      <Box sx={{ color: '#667eea', display: 'flex', '& svg': { fontSize: 16 } }}>
                        {getPatternIcon(p.signal_type)}
                      </Box>
                      <Typography sx={{ fontWeight: 700, fontSize: '0.72rem', color: '#1e293b', flex: 1 }}>{getPatternLabel(p.signal_type)}</Typography>
                      {p.win_rate != null && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <Box sx={{ width: 44, height: 5, borderRadius: 3, bgcolor: '#f0f0f0', overflow: 'hidden' }}>
                            <Box sx={{ width: `${wr}%`, height: '100%', bgcolor: wrColor, borderRadius: 3 }} />
                          </Box>
                          <Typography sx={{ fontSize: '0.65rem', fontWeight: 900, color: wrColor, fontFamily: 'monospace' }}>{wr}%</Typography>
                        </Box>
                      )}
                    </Box>
                    {/* Stats row */}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                      <Typography sx={{ fontSize: '0.55rem', fontWeight: 700 }}>
                        <span style={{ color: '#10b981' }}>{p.wins || 0}W</span>
                        {' / '}
                        <span style={{ color: '#ef4444' }}>{p.losses || 0}L</span>
                        <span style={{ color: '#94a3b8', marginLeft: 4 }}>({p.total} {t('scanner.history.total')} · {p.pending || 0} {t('scanner.history.waiting')})</span>
                      </Typography>
                      {p.avg_return != null && (
                        <Typography sx={{ fontSize: '0.55rem', fontWeight: 800, fontFamily: 'monospace', color: p.avg_return >= 0 ? '#10b981' : '#ef4444' }}>
                          {t('scanner.history.avg')} {p.avg_return > 0 ? '+' : ''}{p.avg_return}%
                        </Typography>
                      )}
                      {p.avg_score != null && (
                        <Typography sx={{ fontSize: '0.55rem', color: '#667eea', fontFamily: 'monospace' }}>
                          Score {p.avg_score}
                        </Typography>
                      )}
                      {resolved >= 3 && p.avg_return != null && (
                        <Tooltip title={p.avg_return >= 2 ? t('scanner.history.patternEffective') : p.avg_return >= 0 ? t('scanner.history.patternOk') : t('scanner.history.patternCaution')} arrow placement="top">
                          <Typography sx={{ fontSize: '0.55rem' }}>
                            {p.avg_return >= 2 ? '⭐' : p.avg_return >= 0 ? '✅' : '⚠️'}
                          </Typography>
                        </Tooltip>
                      )}
                    </Box>
                  </Box>
                );
              })}
            </Box>
          )}
        </Box>
      </Drawer>

      {/* ═══ SNACKBAR ═══ */}
      <Snackbar open={snackbar.open} autoHideDuration={4000} onClose={() => setSnackbar(p => ({ ...p, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}>
        <Alert onClose={() => setSnackbar(p => ({ ...p, open: false }))} severity={snackbar.severity}
          sx={{ fontSize: '0.75rem', borderRadius: 2 }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default StockScanner;
